import type { LoadContext } from "@/core/features/index.ts";
import { fetchModule } from "@/core/storage/moduleCache.ts";
import wasmUrl from "./pkg/audiodsp/audiodsp_bg.wasm?url";

/** Hashed by the bundler, so a new build never serves a stale module from the cache. */
export const AUDIODSP_WASM_URL = wasmUrl;

export type NormalizeMode = "peak" | "lufs";
export type FadeCurveName = "linear" | "equalPower" | "exponential";

/** Filter codes the flat band array uses; must match `filter_name` in the crate. */
export const FILTER_CODES = {
  peaking: 0,
  lowShelf: 1,
  highShelf: 2,
  lowPass: 3,
  highPass: 4,
} as const;

type OwnedFloats = Float32Array<ArrayBuffer>;
type OwnedBytes = Uint8Array<ArrayBuffer>;

/** The module is built single-threaded, so its memory is never a SharedArrayBuffer. */
const asFloats = (values: Float32Array): OwnedFloats => values as OwnedFloats;
const asBytes = (values: Uint8Array): OwnedBytes => values as OwnedBytes;

export type AudioDspModule = {
  buildPeaks(samples: OwnedFloats, channelCount: number): OwnedBytes;
  peakDbfs(samples: OwnedFloats): number;
  integratedLufs(samples: OwnedFloats, channelCount: number, sampleRateHz: number): number;
  normalizeGainDb(
    samples: OwnedFloats,
    channelCount: number,
    sampleRateHz: number,
    mode: NormalizeMode,
    targetDb: number,
  ): number;
  applyGain(samples: OwnedFloats, db: number): OwnedFloats;
  applyFade(
    samples: OwnedFloats,
    channelCount: number,
    sampleRateHz: number,
    inSec: number,
    outSec: number,
    curve: FadeCurveName,
  ): OwnedFloats;
  applyEq(
    samples: OwnedFloats,
    channelCount: number,
    sampleRateHz: number,
    bands: OwnedFloats,
  ): OwnedFloats;
  toMono(samples: OwnedFloats, channelCount: number): OwnedFloats;
  findSilence(
    samples: OwnedFloats,
    channelCount: number,
    sampleRateHz: number,
    thresholdDb: number,
    minDurationSec: number,
  ): OwnedFloats;
  encodeWav(samples: OwnedFloats, channelCount: number, sampleRateHz: number): OwnedBytes;
  encodeFlac(samples: OwnedFloats, channelCount: number, sampleRateHz: number): OwnedBytes;
};

export async function loadAudioDsp(context: LoadContext): Promise<AudioDspModule> {
  const [module, response] = await Promise.all([
    import("./pkg/audiodsp/audiodsp.js"),
    fetchModule(wasmUrl, { signal: context.signal, onProgress: context.report }),
  ]);
  await module.default({ module_or_path: response });
  return {
    buildPeaks: (samples, channels) => asBytes(module.buildPeaks(samples, channels)),
    peakDbfs: module.peakDbfs,
    integratedLufs: module.integratedLufs,
    normalizeGainDb: module.normalizeGainDb,
    applyGain: (samples, db) => asFloats(module.applyGain(samples, db)),
    applyFade: (samples, channels, rate, inSec, outSec, curve) =>
      asFloats(module.applyFade(samples, channels, rate, inSec, outSec, curve)),
    applyEq: (samples, channels, rate, bands) =>
      asFloats(module.applyEq(samples, channels, rate, bands)),
    toMono: (samples, channels) => asFloats(module.toMono(samples, channels)),
    findSilence: (samples, channels, rate, threshold, minDuration) =>
      asFloats(module.findSilence(samples, channels, rate, threshold, minDuration)),
    encodeWav: (samples, channels, rate) => asBytes(module.encodeWav(samples, channels, rate)),
    encodeFlac: (samples, channels, rate) => asBytes(module.encodeFlac(samples, channels, rate)),
  };
}
